import React, { useState, useRef } from 'react';
import { AvatarConfig } from '../../types/user';
import { AvatarDisplay } from './AvatarDisplay';
import { compressAvatarPhoto } from '../../utils/compressMedia';
import { IconCamera } from '../ui/Icons';
import { useToast } from '../../hooks/useToast';

const EMOJIS = [
  '👤','👨','👩','🧓','🧑‍💻','🧑‍🎨','🧑‍🚀','🧙','🧛','🤖',
  '🐼','🦊','🐻','🦁','🐮','🦋','🐢','🐙','🦄','💉',
  '🌵','🌈','⭐','⚡','🔥','🏠','🌍','🚀','🎤','🎸',
];

const COLORS = [
  '#7c3aed','#f59e0b','#10b981','#ef4444','#3b82f6','#ec4899',
  '#8b5cf6','#06b6d4','#84cc16','#f97316','#64748b','#1e293b',
];

const SHAPES: { key: AvatarConfig['shape']; label: string }[] = [
  { key: 'circle',  label: '●' },
  { key: 'rounded', label: '■' },
  { key: 'hex',     label: '⬢' },
];

interface Props {
  initial: AvatarConfig;
  onSave: (cfg: AvatarConfig) => void;
  onClose: () => void;
}

export function AvatarBuilder({ initial, onSave, onClose }: Props) {
  const [cfg, setCfg] = useState<AvatarConfig>(initial);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const { show } = useToast();

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = await compressAvatarPhoto(file);
      setCfg(c => ({ ...c, profilePhoto: data }));
      show('Photo ready — tap Save to apply', 'success');
    } catch (err) {
      console.error('[AvatarBuilder] photo compress failed', err);
      show('Could not load photo, try another image', 'error');
    } finally { setUploading(false); }
    e.target.value = '';
  };

  const removePhoto = () => {
    setCfg(c => ({ ...c, profilePhoto: undefined }));
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Preview */}
      <div className="flex justify-center py-4">
        <AvatarDisplay avatar={cfg} size={80} isMe />
      </div>

      {/* Profile photo */}
      <div>
        <p className="section-label">Profile photo</p>
        <input type="file" accept="image/*" ref={fileRef} onChange={handlePhotoUpload} className="hidden" />
        <div className="flex gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="btn-secondary flex-1 text-sm py-2"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Compressing...
              </span>
            ) : cfg.profilePhoto ? (
              <><IconCamera size={14} className="inline mr-1" /> Change photo</>
            ) : (
              <><IconCamera size={14} className="inline mr-1" /> Upload photo</>
            )}
          </button>
          {cfg.profilePhoto && (
            <button onClick={removePhoto} className="btn-ghost text-sm py-2 text-stabeon-danger">
              Remove
            </button>
          )}
        </div>
        <p className="text-stabeon-muted text-[10px] mt-1">
          {cfg.profilePhoto ? 'Photo will show on your map marker and avatar' : 'Or use an emoji icon below'}
        </p>
      </div>

      {/* Emoji grid — only show if no photo, or collapsed */}
      {!cfg.profilePhoto && (
        <div>
          <p className="section-label">Icon</p>
          <div className="grid grid-cols-10 gap-1">
            {EMOJIS.map(e => (
              <button
                key={e}
                onClick={() => setCfg(c => ({ ...c, emoji: e }))}
                className={`h-8 w-8 rounded-lg text-lg flex items-center justify-center transition-colors ${
                  cfg.emoji === e ? 'bg-stabeon-accent' : 'bg-stabeon-surface hover:bg-stabeon-border'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Background color — only when no photo */}
      {!cfg.profilePhoto && (
        <div>
          <p className="section-label">Background</p>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(c => (
              <button
                key={c}
                onClick={() => setCfg(cfg => ({ ...cfg, bgColor: c }))}
                style={{ background: c }}
                className={`w-8 h-8 rounded-full transition-transform ${
                  cfg.bgColor === c ? 'scale-125 ring-2 ring-white' : 'hover:scale-110'
                }`}
              />
            ))}
          </div>
        </div>
      )}

      {/* Border color */}
      <div>
        <p className="section-label">Border</p>
        <div className="flex flex-wrap gap-2">
          {[...COLORS, '#ffffff22', '#00000000'].map(c => (
            <button
              key={c}
              onClick={() => setCfg(cfg => ({ ...cfg, borderColor: c }))}
              style={{ background: c, border: '1px solid rgba(255,255,255,0.13)' }}
              className={`w-8 h-8 rounded-full transition-transform ${
                cfg.borderColor === c ? 'scale-125 ring-2 ring-white' : 'hover:scale-110'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Shape */}
      <div>
        <p className="section-label">Shape</p>
        <div className="flex gap-3">
          {SHAPES.map(s => (
            <button
              key={s.key}
              onClick={() => setCfg(c => ({ ...c, shape: s.key }))}
              className={`flex-1 py-2 rounded-lg text-xl transition-all ${
                cfg.shape === s.key ? 'pill-active' : 'pill-inactive'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
        <button onClick={() => onSave(cfg)} className="btn-primary flex-1">Save</button>
      </div>
    </div>
  );
}
